import React, { useMemo, useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import MasterDataWorkspace from "@/components/master-data/MasterDataWorkspace";
import MasterDataForm from "@/components/master-data/MasterDataForm";
import { getMasterSchema } from "./MasterDataSchemas";

const MASTER_LABELS = {
  "profil-perusahaan-master":"Profil Perusahaan",
  "master-lokasi":"Lokasi",
  "semua-item":"Semua Item",
  "master-barang-dagangan":"Barang Dagangan",
  "master-perlengkapan":"Perlengkapan",
  "master-peralatan":"Peralatan",
  "master-jasa":"Jasa",
  "kategori-item":"Kategori Item",
  "merek-item":"Merek",
  "satuan-item":"Satuan",
  "konversi-satuan":"Konversi Satuan",
  "varian-item":"Varian",
  "modifier-item":"Modifier",
  "resep-dan-bom-master":"Resep dan BOM",
  "kategori-supplier":"Kategori Supplier",
  "kontak-supplier":"Kontak Supplier",
  "alamat-supplier":"Alamat Supplier",
  "rekening-supplier":"Rekening Supplier",
  "harga-supplier":"Harga Supplier",
  "syarat-pembayaran-supplier":"Syarat Pembayaran Supplier",
  "kategori-pelanggan-master":"Kategori Pelanggan",
  "kontak-pelanggan":"Kontak Pelanggan",
  "alamat-pelanggan":"Alamat Pelanggan",
  "daftar-harga-pelanggan":"Daftar Harga Pelanggan",
  "syarat-pembayaran-pelanggan":"Syarat Pembayaran Pelanggan",
  "kategori-akun":"Kategori Akun",
  "master-kas":"Kas",
  "rekening-bank-master":"Rekening Bank",
  "metode-pembayaran":"Metode Pembayaran",
  "kategori-pendapatan":"Kategori Pendapatan",
  "kategori-beban":"Kategori Beban",
  "master-pajak":"Pajak",
  "dimensi-akuntansi":"Dimensi Akuntansi",
  "periode-akuntansi-master":"Periode Akuntansi",
  "status-kepegawaian":"Status Kepegawaian",
  "komponen-gaji":"Komponen Gaji",
  "komponen-potongan":"Komponen Potongan",
  "jenis-cuti":"Jenis Cuti",
  "jenis-lembur":"Jenis Lembur",
  "pola-jadwal":"Pola Jadwal",
  "template-kpi":"Template KPI",
  "master-aset":"Master Aset",
  "kategori-aset-master":"Kategori Aset",
  "lokasi-aset-master":"Lokasi Aset",
  "metode-penyusutan":"Metode Penyusutan",
  "kategori-pemeliharaan":"Kategori Pemeliharaan",
  "jenis-insiden":"Jenis Insiden",
  "checklist-operasional":"Checklist Operasional",
  "kategori-promosi":"Kategori Promosi",
  "campaign-master":"Campaign",
  "poin-reward-master":"Poin Reward",
  "reward-master":"Reward",
  "kanal-penjualan":"Kanal Penjualan",
  "segmentasi-pelanggan-master":"Segmentasi Pelanggan",
  "daftar-pengguna":"Daftar Pengguna",
  "referensi-role":"Referensi Role",
  "referensi-permission":"Referensi Permission",
  "grup-akses":"Grup Akses",
  "scope-akses":"Scope Akses",
  "penomoran-dokumen":"Penomoran Dokumen",
  "template-dokumen":"Template Dokumen",
  "mata-uang":"Mata Uang",
  "zona-waktu":"Zona Waktu",
  "preferensi-perusahaan":"Preferensi Perusahaan",
  "referensi-integrasi":"Referensi Integrasi"
};

const SLUG_ALIASES = {
  "profil-perusahaan-master": "profil-perusahaan",
  "master-barang-dagangan": "barang-dagangan",
  "master-perlengkapan": "perlengkapan",
  "master-peralatan": "peralatan",
  "master-jasa": "jasa",
  "merek-item": "merek",
  "satuan-item": "satuan",
  "varian-item": "varian",
  "modifier-item": "modifier",
  "resep-dan-bom-master": "resep-dan-bom",
  "kategori-pelanggan": "kategori-pelanggan-master",
  "master-kas": "kas",
  "rekening-bank-master": "rekening-bank",
  "master-pajak": "pajak",
  "periode-akuntansi-master": "periode-akuntansi",
  "campaign-master": "campaign",
  "poin-reward-master": "poin-reward",
  "reward-master": "reward",
  
};

function normalizeStatus(value) {
  return String(value || "ACTIVE").toUpperCase() === "INACTIVE"
    ? "INACTIVE"
    : "ACTIVE";
}

function getDefaultValue(field) {
  if (field.defaultValue !== undefined) return field.defaultValue;
  if (field.name === "status") return "active";
  if (field.type === "checkbox") return false;
  return "";
}

function formatCell(value, field) {
  if (value === undefined || value === null || value === "") return "—";

  if (field.type === "checkbox") return value ? "Ya" : "Tidak";

  if (field.type === "select") {
    const option = (field.options || []).find(
      item => String(item.value ?? item) === String(value)
    );
    return option?.label ?? option?.value ?? value;
  }

  return value;
}

export default function MasterDataCatalog() {
  const { slug } = useParams();
  const [params] = useSearchParams();

  const schemaSlug = SLUG_ALIASES[slug] || slug;
  const title = params.get("title") || MASTER_LABELS[slug] || slug;
  const fields = useMemo(
    () => getMasterSchema(schemaSlug, title).fields || [],
    [schemaSlug, title]
  );

  const MASTER_CATALOG_STORAGE = `mazboy-master-catalog-${schemaSlug}`;

  const [rows, setRows] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(MASTER_CATALOG_STORAGE) || "[]");
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(MASTER_CATALOG_STORAGE, JSON.stringify(rows));
  }, [rows, MASTER_CATALOG_STORAGE]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const columns = useMemo(() => {
    const visibleFields = fields.filter(field => field.type !== "textarea");
    const preferredKeys = ["code", "sku", "name", "status"];
    const selected = [];

    preferredKeys.forEach(key => {
      const field = visibleFields.find(item => item.name === key);
      if (field && !selected.some(item => item.name === field.name)) {
        selected.push(field);
      }
    });

    visibleFields.forEach(field => {
      if (
        selected.length < 6 &&
        !selected.some(item => item.name === field.name)
      ) {
        selected.push(field);
      }
    });

    return selected.map(field => ({
      key: field.name === "status" ? "statusLabel" : field.name,
      label: field.label,
      field
    }));
  }, [fields]);

  const normalizedRows = useMemo(() => rows.map(row => ({
    ...row,
    status: normalizeStatus(row.status),
    statusLabel: normalizeStatus(row.status) === "ACTIVE"
      ? "Aktif"
      : "Nonaktif"
  })), [rows]);

  const tableRows = useMemo(() => normalizedRows.map(row => {
    const result = { ...row, __raw: row };

    columns.forEach(column => {
      const field = column.field;
      result[column.key] = column.key === "statusLabel"
        ? row.statusLabel
        : formatCell(row[field.name], field);
    });

    return result;
  }), [normalizedRows, columns]);

  function createNew() {
    const initial = {};
    fields.forEach(field => {
      initial[field.name] = getDefaultValue(field);
    });
    setEditing(initial);
  }

  function save(values) {
    setSaving(true);

    const item = { ...values };
    if (item.status !== undefined) {
      item.status = normalizeStatus(item.status);
    }

    if (editing?.id) {
      setRows(old => old.map(row =>
        row.id === editing.id ? { ...row, ...item } : row
      ));
    } else {
      setRows(old => [...old, {
        ...item,
        id: `${schemaSlug}-${Date.now()}`
      }]);
    }

    setEditing(null);
    setSaving(false);
  }


  function exportCSV() {
    const cell = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      fields.map(f => cell(f.label)).join(","),
      ...rows.map(r => fields.map(f => cell(r[f.name])).join(","))
    ];
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${schemaSlug}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function importCSV(file) {
    const reader = new FileReader();
    reader.onload = e => {
      const text = String(e.target.result || "").replace(/^\uFEFF/, "");
      const lines = text.split(/\r?\n/).filter(x => x.trim());
      if (lines.length < 2) return alert("CSV kosong.");

      const parse = line => {
        const out = [];
        let value = "", quoted = false;
        for (let i = 0; i < line.length; i++) {
          const c = line[i];
          if (c === '"' && quoted && line[i+1] === '"') {
            value += '"'; i++;
          } else if (c === '"') {
            quoted = !quoted;
          } else if (c === "," && !quoted) {
            out.push(value); value = "";
          } else {
            value += c;
          }
        }
        out.push(value);
        return out;
      };

      const headers = parse(lines[0]).map(x => x.trim());
      const mapped = fields.map(f => ({f, i: headers.indexOf(f.label)}))
        .filter(x => x.i >= 0);
      if (!mapped.length) return alert("Header CSV tidak cocok.");

      const data = lines.slice(1).map((line, n) => {
        const cells = parse(line), row = {};
        fields.forEach(f => row[f.name] = getDefaultValue(f));
        mapped.forEach(({f, i}) => row[f.name] = cells[i] ?? "");
        row.status = normalizeStatus(row.status);
        row.id = `${schemaSlug}-import-${Date.now()}-${n}`;
        return row;
      });

      setRows(prev => [...prev, ...data]);
      alert(`${data.length} data diimpor ke tampilan lokal.`);
    };
    reader.onerror = () => alert("Gagal membaca CSV.");
    reader.readAsText(file, "UTF-8");
  }

  function toggleStatus(row) {
    setRows(old => old.map(item => item.id === row.id
      ? {
          ...item,
          status: normalizeStatus(item.status) === "ACTIVE"
            ? "INACTIVE"
            : "ACTIVE"
        }
      : item
    ));
  }

  return editing ? (
    <section className="mdw">
      <MasterDataForm
        title={`${editing.id ? "Edit" : "Data Baru"} — ${title}`}
        fields={fields}
        initialValues={editing}
        onSubmit={save}
        onCancel={() => setEditing(null)}
        saving={saving}
      />
    </section>
  ) : (
    <MasterDataWorkspace
      title={title}
      columns={columns}
      rows={tableRows}
      onCreate={createNew}
      onEdit={row => setEditing({ ...row.__raw })}
      onRefresh={() => {}}
      onImport={importCSV}
      onExport={exportCSV}
      onToggleStatus={toggleStatus}
    />
  );
}
